/**
 * useMultiTermSearch hook (SBR-3533 - Real GraphQL implementation)
 *
 * Fetches multi-term search results from SGS multiTermSearch GraphQL operation.
 * Follows the pattern from getProductRecommendations.js.
 *
 * @param {Object} options
 * @param {string[]} options.keywords - Array of search terms
 * @param {number} options.resultCount - Number of results per term (default: 4)
 * @returns {{data: Object|null, loading: boolean, error: Error|null}}
 */
import { useState, useEffect } from 'react';
import { gqlClient } from 'services/api/gql/gqlClient';
import { MultiTermSearch } from 'constants/gql/queries/relatedSearches/multiTermSearch.gql';
import localeUtils from 'utils/LanguageLocale';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { validateMultiTermSearchResponse } from 'components/RelatedSearches/schemas/MultiTermSearchResponseSchema';

function useMultiTermSearch({ keywords = [], resultCount = 4 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Don't fetch if no keywords
        if (!keywords || keywords.length === 0) {
            setData(null);
            setLoading(false);
            setError(null);

            return undefined;
        }

        setLoading(true);
        setError(null);

        let isCancelled = false;

        async function fetchMultiTermSearch() {
            try {
                // Extract Constructor.io params (follow getProductRecommendations pattern)
                let sessionId = null;
                let constructorClientId = '';

                if (window.ConstructorioClient) {
                    const { queryParams } = window.ConstructorioTracker.options;
                    sessionId = queryParams.s;
                    constructorClientId = queryParams.i;
                }

                // Extract locale - GraphQL backend expects EN_US format (uppercase with underscore)
                const locale = `${localeUtils.getCurrentLanguage().toUpperCase()}_${localeUtils.getCurrentCountry()}`;

                // Extract profileId if available
                const profileId =
                    Storage.local.getItem(LOCAL_STORAGE.USER_DATA)?.profile?.profileId || Storage.local.getItem(LOCAL_STORAGE.PROFILE_ID);

                const variables = {
                    keywords,
                    resultCount,
                    locale,
                    channel: 'RWD', // Default for web per architecture
                    constructorClientId,
                    sessionId,
                    clientId: profileId || '',
                    userSegments: ['external-app']
                };

                // Call GraphQL using gqlClient (follows getProductRecommendations pattern).
                // maxRetries: 0 per AC -- silent degrade to placeholders on failure, no retry.
                const response = await gqlClient.query({
                    operationName: 'MultiTermSearch',
                    query: MultiTermSearch,
                    variables,
                    maxRetries: 0
                });

                if (isCancelled) {
                    return;
                }

                // Extract multiTermSearch from response
                const multiTermSearchData = response?.multiTermSearch;

                if (!multiTermSearchData) {
                    throw new Error('multiTermSearch field missing from GraphQL response');
                }

                // Filter out null/invalid URLs from thumbnailImages (backend can return nulls)
                if (multiTermSearchData.results) {
                    multiTermSearchData.results = multiTermSearchData.results.map(result => ({
                        ...result,
                        thumbnailImages: (result.thumbnailImages || []).filter(img => img && img.url && img.url !== 'null')
                    }));
                }

                // Validate response before setting data
                const validation = validateMultiTermSearchResponse(multiTermSearchData);

                if (!validation.success) {
                    throw new Error(`Response validation failed: ${validation.error}`);
                }

                // Set the unwrapped multiTermSearch data (not the full response envelope)
                setData(multiTermSearchData);
                setLoading(false);
            } catch (err) {
                if (!isCancelled) {
                    // Silent failure per BR-013
                    setError(err);
                    setData(null);
                    setLoading(false);
                }
            }
        }

        fetchMultiTermSearch();

        // Cleanup function to prevent setState on unmounted component
        return () => {
            isCancelled = true;
        };
    }, [keywords.join('|'), resultCount]);

    return { data, loading, error };
}

export default useMultiTermSearch;
